export type SessionGameKind = "arena" | "puzzle";

export interface PuzzleSessionCredentials {
  matchId: string;
  playerId: string;
  ticket: string;
  gameKind: SessionGameKind;
  bracketRound?: string;
  tournamentStatus?: string;
}

export async function resolvePuzzleSession(): Promise<PuzzleSessionCredentials> {
  const query = new URLSearchParams(location.search);
  const invite = query.get("invite") ?? query.get("token");
  let matchId = query.get("match") ?? "";
  let playerId = "";
  let ticket = query.get("ticket") ?? "";
  let gameKind: SessionGameKind = "puzzle";

  if (invite) {
    const response = await fetch("/api/invites/redeem", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ token: invite }),
    });
    if (!response.ok) throw new Error(`초대 승인 실패 (${response.status})`);
    const body = await response.json() as Record<string, unknown>;
    const player = record(body.player);
    matchId = stringValue(body.matchId) || stringValue(player.matchId) || matchId;
    playerId = stringValue(player.id) || stringValue(body.playerId);
    ticket = stringValue(body.ticket) || stringValue(body.wsTicket) || ticket;
    gameKind = body.gameKind === "arena" ? "arena" : "puzzle";
  }

  if (matchId && (!ticket || !playerId)) {
    const renewed = await renewPuzzleSession(matchId);
    matchId = renewed.matchId;
    playerId = renewed.playerId;
    ticket = renewed.ticket;
    gameKind = renewed.gameKind;
  }
  if (!matchId || !playerId || !ticket) {
    throw new Error("초대 링크에 유효한 경기 정보가 없습니다.");
  }
  return { matchId, playerId, ticket, gameKind };
}

export async function renewPuzzleSession(matchId: string): Promise<PuzzleSessionCredentials> {
  const response = await fetch("/api/ws-ticket", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ matchId }),
  });
  if (!response.ok) throw new Error(`경기 티켓 발급 실패 (${response.status})`);
  const body = await response.json() as Record<string, unknown>;
  const player = record(body.player);
  const renewedMatchId = stringValue(body.matchId) || stringValue(player.matchId) || matchId;
  const playerId = stringValue(body.playerId) || stringValue(player.id);
  const ticket = stringValue(body.ticket) || stringValue(body.wsTicket);
  if (!renewedMatchId || !playerId || !ticket) {
    throw new Error("서버가 유효한 경기 티켓을 반환하지 않았습니다.");
  }
  const credentials: PuzzleSessionCredentials = {
    matchId: renewedMatchId,
    playerId,
    ticket,
    gameKind: body.gameKind === "arena" ? "arena" : "puzzle",
  };
  if (typeof body.bracketRound === "string") credentials.bracketRound = body.bracketRound;
  if (typeof body.tournamentStatus === "string") credentials.tournamentStatus = body.tournamentStatus;
  return credentials;
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}
